import { cn } from '@/lib/utils';

/**
 * ZKTeco attendance log: dwInOutMode (SDK "Biometric Device SDK — Programmers Guide", GetGeneralLogData).
 * 0 Duty On, 1 Duty Off, 2 Overtime Begin, 3 Overtime End, 4 Lock Out, 5 Lock In.
 *
 * Stored EventType strings must match what ZkAttendanceSync sends (ZkTecoDevice.GetEventType).
 */
export const ZK_INOUT_MODE_LABELS: Record<number, string> = {
  0: 'In',
  1: 'Out',
  2: 'Overtime begin',
  3: 'Overtime end',
  4: 'Lock out',
  5: 'Lock in',
};

/** Filter / API event type values (canonical) */
export const ZK_EVENT_TYPE_OPTIONS = [
  { value: 'IN', label: 'In', sdkNote: 'dwInOutMode 0 — Duty On' },
  { value: 'OUT', label: 'Out', sdkNote: 'dwInOutMode 1 — Duty Off' },
  { value: 'OVERTIME_START', label: 'Overtime begin', sdkNote: 'dwInOutMode 2' },
  { value: 'OVERTIME_END', label: 'Overtime end', sdkNote: 'dwInOutMode 3' },
  { value: 'LOCK_OUT', label: 'Lock out', sdkNote: 'dwInOutMode 4' },
  { value: 'LOCK_IN', label: 'Lock in', sdkNote: 'dwInOutMode 5' },
  { value: 'PUNCH', label: 'Other / unknown', sdkNote: 'Unmapped code' },
] as const;

/** Radix Select requires non-empty value; maps to null in API. */
export const DEVICE_DEFAULT_EVENT_TYPE_TRUST_VALUE = '__trust_device__';

/** Dropdown values for device default EventType (trust + canonical types). */
export const DEVICE_DEFAULT_EVENT_TYPE_FORM_VALUES = [
  DEVICE_DEFAULT_EVENT_TYPE_TRUST_VALUE,
  ...ZK_EVENT_TYPE_OPTIONS.map((o) => o.value),
] as const;

export function deviceDefaultEventTypeToFormValue(api: string | null | undefined): string {
  if (!api?.trim()) return DEVICE_DEFAULT_EVENT_TYPE_TRUST_VALUE;
  return api.trim().toUpperCase();
}

export function formValueToDeviceDefaultEventType(form: string): string | null {
  if (form === DEVICE_DEFAULT_EVENT_TYPE_TRUST_VALUE || !form.trim()) return null;
  return form.trim().toUpperCase();
}

/** Display labels for stored EventType (includes legacy / mistaken mappings) */
const EVENT_TYPE_DISPLAY: Record<string, string> = {
  IN: 'In',
  OUT: 'Out',
  OVERTIME_START: 'Overtime begin',
  OVERTIME_END: 'Overtime end',
  LOCK_OUT: 'Lock out',
  LOCK_IN: 'Lock in',
  PUNCH: 'Other',
  // Legacy UI / older mappings
  CheckIn: 'In',
  CheckOut: 'Out',
  BreakIn: 'Break in',
  BreakOut: 'Break out',
  BREAK_START: 'Break start',
  BREAK_END: 'Break end',
  BreakStart: 'Break start',
  BreakEnd: 'Break end',
};

/** Prefer API displayEventType (BreakZone semantic) over raw eventType. */
export function resolvePunchLabelEventType(punch: {
  eventType?: string | null;
  displayEventType?: string | null;
}): string {
  const display = punch.displayEventType?.trim();
  if (display) return display;
  return punch.eventType?.trim() || '';
}

export function getZkEventTypeLabel(eventType: string | undefined | null): string {
  if (eventType == null || eventType === '') return '—';
  const direct = EVENT_TYPE_DISPLAY[eventType];
  if (direct) return direct;
  const upper = eventType.toUpperCase();
  for (const [k, v] of Object.entries(EVENT_TYPE_DISPLAY)) {
    if (k.toUpperCase() === upper) return v;
  }
  return eventType;
}

type EventTypeTranslateFn = (
  key: string,
  options?: { defaultValue?: string; mode?: number }
) => string;

/** Uses `attendance` namespace keys (`eventType_IN`, …) when present. */
export function getZkEventTypeLabelTranslated(
  eventType: string | undefined | null,
  t: EventTypeTranslateFn
): string {
  if (eventType == null || eventType === '') return '—';
  const trimmed = eventType.trim();
  const exactKey = `eventType_${trimmed}`;
  const exact = t(exactKey, { defaultValue: '' });
  if (exact && exact !== exactKey) return exact;
  const upper = trimmed.toUpperCase();
  return t(`eventType_${upper}`, { defaultValue: getZkEventTypeLabel(eventType) });
}

export function eventTypesMatch(filterValue: string, recordValue: string | undefined | null): boolean {
  if (filterValue === 'all') return true;
  const a = (recordValue ?? '').trim();
  const b = filterValue.trim();
  return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
}

/** Soft UI palette for event type pills (avoids harsh primary/destructive black-red). */
export function getZkEventTypeBadgeClassName(eventType: string | undefined | null): string {
  const t = (eventType ?? '').trim().toUpperCase();
  const base =
    'border font-medium shadow-none bg-opacity-100 dark:border-opacity-60';
  if (!t)
    return cn(base, 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/50 dark:text-slate-300');

  if (t === 'IN' || t === 'LOCK_IN' || t === 'CHECKIN')
    return cn(
      base,
      'bg-emerald-50 text-emerald-900 border-emerald-200/90 dark:bg-emerald-950/50 dark:text-emerald-100 dark:border-emerald-800/70'
    );
  if (t === 'OUT' || t === 'LOCK_OUT' || t === 'CHECKOUT')
    return cn(
      base,
      'bg-sky-50 text-sky-900 border-sky-200/90 dark:bg-sky-950/45 dark:text-sky-100 dark:border-sky-800/60'
    );
  if (t === 'OVERTIME_START' || t === 'OVERTIME_END')
    return cn(
      base,
      'bg-amber-50 text-amber-950 border-amber-200/90 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800/60'
    );
  if (t === 'PUNCH')
    return cn(base, 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/50 dark:text-slate-200');
  if (t.includes('BREAK'))
    return cn(
      base,
      'bg-indigo-50 text-indigo-900 border-indigo-200/80 dark:bg-indigo-950/45 dark:text-indigo-100'
    );
  return cn(base, 'bg-violet-50 text-violet-900 border-violet-200/80 dark:bg-violet-950/40 dark:text-violet-100');
}

/**
 * SSR_GetGeneralLogData has two tables. Simple is 0–2. Any value >= 3 proves the multi table
 * for that device. Card-only is 2 on simple and 4 on multi. Combined modes are not card-only.
 */
export type ZkVerifyTable = 'simple' | 'multi';

const SIMPLE_VERIFY_METHODS: Record<number, string> = {
  0: 'password',
  1: 'fingerprint',
  2: 'card',
};

const MULTI_VERIFY_METHODS: Record<number, string> = {
  0: 'any',
  1: 'fingerprint',
  2: 'employeeId',
  3: 'password',
  4: 'card',
  5: 'fingerprintOrPassword',
  6: 'fingerprintOrCard',
  7: 'passwordOrCard',
  8: 'employeeIdAndFingerprint',
  9: 'fingerprintAndPassword',
  10: 'fingerprintAndCard',
  11: 'passwordAndCard',
  12: 'fingerprintPasswordAndCard',
  13: 'employeeIdFingerprintAndPassword',
  14: 'fingerprintAndCardOrEmployeeId',
  15: 'face',
};

const VERIFY_METHOD_LABELS: Record<string, string> = {
  password: 'Password',
  fingerprint: 'Fingerprint',
  card: 'Card',
  employeeId: 'Employee ID',
  any: 'Fingerprint, password, or card',
  fingerprintOrPassword: 'Fingerprint or password',
  fingerprintOrCard: 'Fingerprint or card',
  passwordOrCard: 'Password or card',
  employeeIdAndFingerprint: 'Employee ID + fingerprint',
  fingerprintAndPassword: 'Fingerprint + password',
  fingerprintAndCard: 'Fingerprint + card',
  passwordAndCard: 'Password + card',
  fingerprintPasswordAndCard: 'Fingerprint + password + card',
  employeeIdFingerprintAndPassword: 'Employee ID + fingerprint + password',
  fingerprintAndCardOrEmployeeId: 'Fingerprint + card, or employee ID',
  face: 'Face',
};

export function resolveVerifyTable(
  mode: number | undefined | null,
  table?: ZkVerifyTable | null
): ZkVerifyTable {
  if (table === 'simple' || table === 'multi') return table;
  return mode != null && mode >= 3 ? 'multi' : 'simple';
}

export function resolveVerifyMethod(
  mode: number | undefined | null,
  table?: ZkVerifyTable | null
): string | null {
  if (mode == null || Number.isNaN(mode)) return null;
  const map = resolveVerifyTable(mode, table) === 'multi' ? MULTI_VERIFY_METHODS : SIMPLE_VERIFY_METHODS;
  return map[mode] ?? null;
}

export function getVerifyModeLabel(
  mode: number | undefined | null,
  table?: ZkVerifyTable | null
): string {
  const method = resolveVerifyMethod(mode, table);
  if (!method) return mode == null || Number.isNaN(mode) ? '—' : `Verify ${mode}`;
  return VERIFY_METHOD_LABELS[method] ?? method;
}

export function getVerifyModeLabelTranslated(
  mode: number | undefined | null,
  t: EventTypeTranslateFn,
  table?: ZkVerifyTable | null
): string {
  if (mode == null || Number.isNaN(mode)) return '—';
  const method = resolveVerifyMethod(mode, table);
  if (!method) return t('verifyModeUnknown', { defaultValue: `Verify ${mode}`, mode });
  const key = `verifyMethod_${method}`;
  const exact = t(key, { defaultValue: '' });
  if (exact && exact !== key) return exact;
  return t('verifyModeUnknown', { defaultValue: getVerifyModeLabel(mode, table), mode });
}

export function getVerifyModeBadgeClassName(
  mode: number | undefined | null,
  table?: ZkVerifyTable | null
): string {
  const base = 'border font-medium shadow-none';
  const method = resolveVerifyMethod(mode, table);
  if (!method)
    return cn(base, 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-900/40 dark:text-slate-400');
  if (method === 'card')
    return cn(base, 'bg-blue-50 text-blue-900 border-blue-200/90 dark:bg-blue-950/45');
  if (method === 'fingerprint')
    return cn(
      base,
      'bg-teal-50 text-teal-900 border-teal-200/90 dark:bg-teal-950/45 dark:text-teal-100'
    );
  if (method === 'password')
    return cn(base, 'bg-orange-50 text-orange-900 border-orange-200/90 dark:bg-orange-950/40');
  if (method === 'face')
    return cn(base, 'bg-purple-50 text-purple-900 border-purple-200/85 dark:bg-purple-950/40');
  if (method === 'employeeId' || method === 'any')
    return cn(base, 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/50');
  return cn(base, 'bg-cyan-50 text-cyan-900 border-cyan-200/85 dark:bg-cyan-950/35');
}

/** Short label for InOut column (matches dwInOutMode). */
export function getInOutModeShortLabel(mode: number | undefined | null): string {
  if (mode == null || Number.isNaN(mode)) return '—';
  const labels: Record<number, string> = {
    0: 'In',
    1: 'Out',
    2: 'OT begin',
    3: 'OT end',
    4: 'Lock out',
    5: 'Lock in',
  };
  return labels[mode] ?? `Mode ${mode}`;
}

export function getInOutModeBadgeClassName(mode: number | undefined | null): string {
  const base = 'border font-medium shadow-none';
  if (mode == null || Number.isNaN(mode))
    return cn(base, 'bg-slate-50 text-slate-500 border-slate-200');
  switch (mode) {
    case 0:
      return cn(
        base,
        'bg-emerald-50 text-emerald-900 border-emerald-200/90 dark:bg-emerald-950/45 dark:text-emerald-100'
      );
    case 1:
      return cn(base, 'bg-sky-50 text-sky-900 border-sky-200/90 dark:bg-sky-950/40');
    case 2:
    case 3:
      return cn(base, 'bg-amber-50 text-amber-950 border-amber-200/90 dark:bg-amber-950/35');
    case 4:
    case 5:
      return cn(base, 'bg-violet-50 text-violet-900 border-violet-200/85 dark:bg-violet-950/35');
    default:
      return cn(base, 'bg-slate-50 text-slate-700 border-slate-200');
  }
}
